/**
 * Haptic feedback: Capacitor Haptics on native builds (loaded from the native entry
 * chunk, NativeLoaders.haptics), the Vibration API on the web (Android browsers; iOS
 * Safari ignores it). Respects the settings toggle on every call.
 */
import type { Loader } from '../lazy.js';
import type { Platform } from './detect.js';

export type ImpactStyle = 'light' | 'medium' | 'heavy';
export type NotifyType = 'success' | 'warning' | 'error';

export interface Haptics {
  impact(style?: ImpactStyle): void;
  notify(type: NotifyType): void;
  selection(): void;
}

/** The part of @capacitor/haptics used here (ImpactStyle / NotificationType enum values). */
export interface CapacitorHapticsModule {
  Haptics: {
    impact(o?: { style: 'LIGHT' | 'MEDIUM' | 'HEAVY' }): Promise<void>;
    notification(o?: { type: 'SUCCESS' | 'WARNING' | 'ERROR' }): Promise<void>;
    selectionStart(): Promise<void>;
    selectionChanged(): Promise<void>;
  };
}

const IMPACT_MS: Record<ImpactStyle, number> = { light: 8, medium: 15, heavy: 30 };
const NOTIFY_PATTERN: Record<NotifyType, number[]> = { success: [12, 60, 12], warning: [20, 80, 20], error: [30, 60, 30, 60, 30] };

/** The Vibration API; `navigator` satisfies it. */
export interface Vibrator {
  vibrate?(pattern: number | number[]): boolean;
}

export function webHaptics(enabled: () => boolean, nav: Vibrator | undefined = typeof navigator === 'undefined' ? undefined : navigator): Haptics {
  const vibrate = (p: number | number[]) => {
    if (enabled() && typeof nav?.vibrate === 'function') nav.vibrate(p);
  };
  return {
    impact: (style = 'light') => vibrate(IMPACT_MS[style]),
    notify: (type) => vibrate(NOTIFY_PATTERN[type]),
    selection: () => vibrate(5),
  };
}

export function nativeHaptics(enabled: () => boolean, load: Loader<CapacitorHapticsModule>): Haptics {
  let selecting: Promise<void> | null = null;
  // fire and forget: a failed load or call must never reach the game's unhandledrejection boundary
  const run = (fn: (h: CapacitorHapticsModule['Haptics']) => Promise<void>) => {
    if (enabled()) load().then(({ Haptics }) => fn(Haptics)).catch(() => undefined);
  };
  return {
    impact: (style = 'light') => run((h) => h.impact({ style: style.toUpperCase() as 'LIGHT' | 'MEDIUM' | 'HEAVY' })),
    notify: (type) => run((h) => h.notification({ type: type.toUpperCase() as 'SUCCESS' | 'WARNING' | 'ERROR' })),
    // selectionChanged does nothing outside a selectionStart() session (iOS and Android); one session serves them all
    selection: () =>
      run(async (h) => {
        await (selecting ??= h.selectionStart());
        await h.selectionChanged();
      }),
  };
}

/** Native builds must pass the Haptics loader: a bare specifier does not resolve inside the WebView. */
export function createHaptics(platform: Platform, enabled: () => boolean, load?: Loader<CapacitorHapticsModule>): Haptics {
  if (!platform.native) return webHaptics(enabled);
  if (!load) throw new Error(`createHaptics: ${platform.kind} needs the @capacitor/haptics loader (NativeLoaders.haptics)`);
  return nativeHaptics(enabled, load);
}
